import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, TextField, Button, Grid, Card, CardContent, Pagination, Alert, IconButton } from '@mui/material';
import Spinner from '../../components/Spinner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { speakText } from '../../utils/speech';

const Idioms: React.FC = () => {
  const navigate = useNavigate();
  const [idioms, setIdioms] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 12;

  const fetchIdioms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let queryStr = `?type=idiom&page=${page}&limit=${limit}`;
      if (search.trim()) {
        queryStr += `&search=${encodeURIComponent(search.trim())}`;
      }

      const res = await api.get(`/language-hub${queryStr}`);
      if (res.data && res.data.success) {
        setIdioms(res.data.data);
        setTotalPages(res.data.pagination.pages || 1);
      } else {
        setError('Failed to fetch idioms.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error connecting to idioms API');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchIdioms();
  }, [fetchIdioms]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchIdioms();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back navigation */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/language-hub')} 
        sx={{ mb: 4 }}
      >
        Language Hub
      </Button>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
          Idioms & Sayings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Understand figurative language. Learn the meanings and see usage examples for common English idioms.
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 5 }}>
        <form onSubmit={handleSearchSubmit}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for an idiom..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
            />
            <Button type="submit" variant="contained" size="large" sx={{ px: 4 }}>
              Search
            </Button>
          </Box>
        </form>
      </Box>

      {/* Error display */}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {/* Idioms grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Spinner />
        </Box>
      ) : idioms.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">No idioms found matching your query.</Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {idioms.map((item) => (
              <Grid item xs={12} sm={6} key={item._id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 4, 
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.04) 0%, rgba(236, 72, 153, 0.01) 100%)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(236, 72, 153, 0.02) 100%)',
                      borderColor: '#ec4899'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h5" component="h2" sx={{ flex: 1, fontWeight: 800, color: '#f472b6' }}>
                        "{item.idiom}"
                      </Typography>
                      <IconButton
                        type="button"
                        aria-label={`Read aloud: ${item.idiom}`}
                        onClick={() => speakText([
                          item.idiom,
                          `Meaning: ${item.meaning}`,
                          ...(item.examples || []).map((example: string) => `Example: ${example}`),
                        ].filter(Boolean).join('. '))}
                        size="small"
                        sx={{ flexShrink: 0 }}
                      >
                        <VolumeUpIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3, fontWeight: 500, color: 'text.primary', lineHeight: 1.6 }}>
                      <strong>Meaning:</strong> {item.meaning}
                    </Typography>
                    {item.examples && item.examples.length > 0 && (
                      <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.02)', borderLeft: '3px solid #ec4899' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                          Examples
                        </Typography>
                        {item.examples.map((ex: string, index: number) => (
                          <Typography key={index} variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: index < item.examples.length - 1 ? 1 : 0 }}>
                            • "{ex}"
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(e, p) => setPage(p)} 
                color="primary" 
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Idioms;
